<template>
        <div class="email-template">
            <h1 class="text-left">Edit an Email Template</h1>

            <ValidationObserver ref="emailTemplateForm" tag="form" v-on:submit.prevent="saveContent()">
                <div class="email-editor">
                    <div class="email-editor-wrapper" ref="editor">
                        <div ref="templateMeta" class="email-meta">
                            <div class="email-meta__subject-send">
                                <label for="subject">Subject <variables v-bind:variables="variablesCopy" v-bind:obj="emailTemplate" v-bind:propertyName="'subject'" v-bind:inputText="subjectInput" v-bind:popupPosition="'bottom-right'"></variables></label>
                                <button v-on:click.prevent.stop="showFileSelector" class="btn btn-secondary"><i class="fas fa-paperclip"></i></button>
                                <button v-on:click.prevent="sendTestEmail" class="btn btn-secondary">Send test email</button>
                            </div>
                            <div>
                                <ValidationProvider rules="required|min:3" v-slot="{ errors }">
                                    <input v-model="emailTemplate.subject" v-on:keydown="changed = true" id="subject" ref="subject" type="text" class="form-account__text short-question">
                                    <span class="form__error" v-if="errors">{{ errors[0] }}</span>
                                </ValidationProvider>
                            </div>
                        </div>
                        <div class="email-content-wrapper">
                            <div ref="textarea" class="email-content"></div>
                        </div>
                        <transition name="slide">
                        <div v-if="attachments.length > 0" ref="templateAttachments" class="email-editor-attachments">
                            <transition-group name="list" tag="ul">
                                <li v-for="(attachment, index) of attachments" v-bind:key="attachment.reference">
                                    <i class="fas fa-paperclip"></i>
                                    {{attachment.filename}}
                                    <i v-on:click="detachFile(index)" class="fas fa-times"></i>
                                </li>
                            </transition-group>
                        </div>
                        </transition>
                    </div>

                    <div v-if="updating || error" class="infobar" v-bind:class="{'infobar-info' : updating, 'infobar-error' : error}">
                        {{infoMessage}}
                    </div>

                    <div class="row" v-show="!updating">
                        <div class="col-6 offset-3">
                            <div class="row email-editor-buttons">
                                <div class="col-6">
                                    <button class="btn btn-secondary" v-on:click.prevent="cancel">Cancel</button>
                                </div>
                                <div class="col-6">
                                    <button class="btn btn-primary" v-bind:aria-disabled="!changed" v-bind:class="{'btn-disabled' : !changed}">Save</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </ValidationObserver>

            <confirm-dialog v-bind:continuePrimaryText="'Save'"
                            v-bind:message="confirmLoseChangesMessage"
                            v-bind:callbackPrimaryName="'save-email-template'"
                            v-bind:callbackPrimaryReference="'save'"
                            v-if="showConfirmLoseChangesDialog"/>

            <file-selector-dialog v-bind:files="getFiles"
                                  v-if="showFileSelectorDialog"/>
        </div>
</template>

<script>
    import jQuery from "jquery";
    import "bootstrap"
    import "summernote/dist/summernote-bs4.js"
    /* Moved the CSS file to main.js, so that the fonts load properly. Else problem, loading twice? */
    import "summernote-cleaner/summernote-cleaner.js"
    import { ValidationObserver, ValidationProvider } from 'vee-validate'

    import { store, getters } from '../store.js'
    import Variables from "../components/establishments/Variables";
    import FileSelectorDialog from '../components/establishments/FileSelectorDialog'
    import ConfirmDialog from '../components/ConfirmDialog.vue'

    import { variablesMixin } from '../mixins/VariablesMixin'

    export default {
        name: 'EditEmailTemplate',
        components: {
            ValidationObserver, ValidationProvider, Variables, FileSelectorDialog, ConfirmDialog
        },
        mixins: [ variablesMixin ],
        data() {
            return {
                reference: null,
                subjectInput: null,
                emailTemplate: {
                    name: "",
                    subject: "",
                },
                attachments: [],
                requiredVariables: [],
                changed: false,
                cancelling: false,
                updating: false,
                error: false,
                infoMessage: "",
                confirmLoseChangesMessage: "You have un-saved changes to this email template. Would you like to save your work before leaving the page?",
                showConfirmLoseChangesDialog: false,
                showFileSelectorDialog: false,
                navigatingToRoute: ""
            }
        },
        computed: {
            getUnitTypeForDisplay: function() {
                return getters.getCurrentEstablishment().preferences.displayCategoryForRoomTypes;
            },
            getFiles: function() {
                return getters.getCurrentEstablishment().preferences.files;
            }
        },
        methods: {
            cancel: function() {
                console.log("cancel");
                this.cancelling = true;
                this.$router.push({name: 'Automations'});
            },
            showFileSelector: function() {
                console.log("showing file selector");
                this.showFileSelectorDialog = true;
            },
            attachFiles: async function(files) {
                console.log(files);
                const initialHeight = (this.$refs.templateAttachments) ? this.$refs.templateAttachments.clientHeight : 0;
                this.showFileSelectorDialog = false;
                for (const file of files) {
                    const success = await this.addFileToTemplate(file);
                    if (!success) {
                        break;
                    }
                    this.attachments.push(file);
                    this.updateEditorHeightAfterUpdatingAttachments(initialHeight);
                }
            },
            addFileToTemplate: async function(file) {
                this.error = false;
                console.log("Attach file: " + file.reference + " to email template: " + this.reference);
                try {
                    await this.$http.put('/guest-management/v1/establishments/' + store.currentEstablishmentReference + '/email-templates/' + this.reference + "/attachments/" + file.reference);
                    return true;
                } catch (error) {
                    this.error = true;
                    if (error.response) {
                        this.infoMessage = error.response.data.message
                    } else {
                        this.infoMessage = "There was an error attaching your file. Please try again"
                    }
                    return false;
                }
            },
            detachFile: async function(index) {
                console.log("Removing attachment at index: " + index);
                if (index < 0 || index > this.attachments.length) {
                    return;
                }
                const initialHeight = (this.$refs.templateAttachments) ? this.$refs.templateAttachments.clientHeight : 0;
                const success = await this.deleteFileFromTemplate(this.attachments[index]);
                if (!success) {
                    return;
                }
                this.attachments.splice(index, 1);
                setTimeout(() => this.updateEditorHeightAfterUpdatingAttachments(initialHeight), 500);
            },
            deleteFileFromTemplate: async function(file) {
                this.error = false;
                console.log("Delete file: " + file.reference + " from email template: " + this.reference);
                try {
                    await this.$http.delete('/guest-management/v1/establishments/' + store.currentEstablishmentReference + '/email-templates/' + this.reference + "/attachments/" + file.reference);
                    return true;
                } catch (error) {
                    this.error = true;
                    if (error.response) {
                        this.infoMessage = error.response.data.message
                    } else {
                        this.infoMessage = "There was an error removing your file. Please try again"
                    }
                    return false;
                }
            },
            updateEditorHeightAfterUpdatingAttachments: function(initialHeight) {
                this.$nextTick(() => {
                    const currentHeight = (this.$refs.templateAttachments) ? this.$refs.templateAttachments.clientHeight : 0;
                    if (initialHeight !== currentHeight) {
                        console.log("Heights differ. Initial: " + initialHeight + " vs current: " + currentHeight);
                        let height = jQuery('div.note-editable').height();
                        height = height - (currentHeight - initialHeight);
                        jQuery('div.note-editable').height(height);
                    }
                });
            },
            validateEmailContent(content) {
                console.log("validating email content");
                console.log(content);
                for (let requiredVariable of this.requiredVariables) {
                    let variable = '${' + requiredVariable.name + '}';
                    if (content.indexOf(variable) < 0) {
                        this.error = true;
                        this.infoMessage = "Your email is missing the required variable '" + variable + "', which is required in order to send this email to guests.";
                        return false;
                    }
                }
                return true;
            },
            saveContent: async function() {
                console.log("saving");
                const content = this.editorElement.summernote('code');
                if (this.validateEmailContent(content)) {
                    try {
                        const success = await this.$refs.emailTemplateForm.validate();
                        console.log("Validated:" + success);
                        if (!success) {
                            return;
                        }
                        this.updating = true;
                        this.error = false;
                        this.infoMessage = "Saving email template";

                        const body = {
                            name: "" + this.emailTemplate.name,
                            subject: "" + this.emailTemplate.subject,
                            content: "" + content,
                        };

                        const response = await this.$http.put('/guest-management/v1/establishments/' + store.currentEstablishmentReference + '/email-templates/' + this.reference, body);
                        console.log("Email template updated. Response: " + response);
                        this.infoMessage = "Email template saved";
                        this.changed = false;
                        setTimeout(() => {
                            this.updating = false;
                        }, 2000);
                    } catch (error) {
                        this.updating = false;
                        this.error = true;
                        if (error.response) {
                            this.infoMessage = error.response.data.message;
                        } else {
                            this.infoMessage = "Sorry, we can't seem to reach our servers! Please try again";
                        }
                    }
                }
            },
            sendTestEmail: async function () {
                console.log("sending a test email");

                this.updating = true;
                this.error = false;
                this.infoMessage = "Sending test email";

                try {
                    const body = {
                        email: store.host.email
                    };

                    await this.$http.post('/guest-management/v1/establishments/' + store.currentEstablishmentReference + '/email-templates/' + this.reference + '/test-send', body);
                    console.log("Test email sent");
                    this.infoMessage = "Test email sent";
                    setTimeout(() => {
                        this.updating = false;
                        this.infoMessage = "";
                    }, 2000);
                } catch (error) {
                    this.updating = false;
                    this.error = true;
                    if (error.response) {
                        this.infoMessage = error.response.data.message;
                    } else {
                        this.infoMessage = "Sorry, we can't seem to reach our servers! Please try again";
                    }
                }
            },
            personalisationButton: function (context) {
                var ui = jQuery.summernote.ui;

                var list = "";
                for (let variable of this.generalVariables) {
                    list = list + "<li>" + variable.name + "</li>";
                }
                list = list + "<li class='email-editor-toolbar-dropdown__separator text-capitalize'>" + this.getUnitTypeForDisplay + " variables</li>";
                for (let roomTypeVariable of this.roomTypeVariables) {
                    list = list + "<li>" + roomTypeVariable.name + "</li>";
                }

                // create button
                var button = ui.buttonGroup([
                    ui.button({
                        className: 'dropdown-toggle email-editor-toolbar-button-custom',
                        contents: '<strong>${...}</strong>',
                        tooltip: 'Personalisation variable',
                        data: {
                            toggle: 'dropdown'
                        }
                    }),
                    ui.dropdown({
                        className: 'email-editor-toolbar-dropdown',
                        contents: "<ul style=\"max-height: 50vh; overflow-y: scroll;\">" + list + "</ul>",
                        callback: function ($dropdown) {
                            console.log($dropdown);
                            $dropdown.find('li').each(function () {
                                jQuery(this).click(function (e) {
                                    context.invoke('editor.restoreRange');
                                    context.invoke('editor.focus');
                                    context.invoke("editor.insertText", "${" + jQuery(this).html() + "}");
                                    e.preventDefault();
                                });
                            });
                        }
                    })
                ]);

                return button.render();   // return button as jquery object
            },
            formLinkButton: function(context) {
                let ui = jQuery.summernote.ui;

                // create button
                let button = ui.buttonGroup([
                    ui.button({
                        className: 'dropdown-toggle email-editor-toolbar-button-custom',
                        contents: '<i class="fab fa-wpforms"></i>',
                        tooltip: 'Link to the form',
                        data: {
                            toggle: 'dropdown'
                        }
                    }),
                    ui.dropdown({
                        className: 'email-editor-toolbar-dropdown',
                        contents: "<ul><li style=\"color: #007bff; text-decoration: underline\">Text link</li><li><span style=\"background-color: #B1D247; padding: 2px 5px; border: none; border-radius: 3px;\">Button</span></li></ul>",
                        callback: function ($dropdown) {
                            console.log($dropdown);
                            $dropdown.find('li').first().click(function (e) {
                                context.invoke('editor.restoreRange');
                                context.invoke('editor.focus');

                                let node = document.createElement('a');
                                node.innerHTML = "&nbsp;complete this form&nbsp;";
                                node.style.textDecoration = 'none';
                                node.setAttribute('style', "text-decoration: none");
                                node.setAttribute('href', "${formURL}");
                                node.setAttribute('target', "_blank");

                                context.invoke('editor.insertNode', node);
                                e.preventDefault();
                            });
                            $dropdown.find('li').last().click(function (e) {
                                context.invoke('editor.restoreRange');
                                context.invoke('editor.focus');

                                let span = document.createElement('span');
                                span.innerHTML = "&nbsp;COMPLETE THIS FORM&nbsp;";
                                span.style.backgroundColor = '#B1D247';
                                span.style.color = '#ffffff';
                                span.style.fontWeight = 'bold';
                                span.style.padding = '12px 16px';
                                span.style.borderRadius = '4px';
                                span.style.textDecoration = 'none';
                                span.style.textTransform = 'uppercase';

                                let node = document.createElement('a');
                                node.appendChild(span);
                                node.style.textDecoration = 'none';
                                node.setAttribute('style', "text-decoration: none !important");
                                node.setAttribute('href', "${formURL}");
                                node.setAttribute('target', "_blank");

                                context.invoke('editor.insertNode', node);
                                e.preventDefault();
                            });
                        }
                    })
                ]);

                return button.render();   // return button as jquery object
            },
            styleCTAButton: function() {
                var ui = jQuery.summernote.ui;

                // create button
                var button = ui.buttonGroup([
                    ui.button({
                        className: 'email-editor-toolbar-button-custom',
                        contents: '<span style="background-color: #CCCCCC; padding: 2px 5px; border: none; border-radius: 3px;"><i class="fas fa-hand-pointer"></i></span>',
                        tooltip: 'Style a button',
                        click: function () {
                            const highlight = window.getSelection();
                            const range = highlight.getRangeAt(0);
                            let span = document.createElement('span');

                            span.innerHTML = highlight;
                            span.style.backgroundColor = '#B1D247';
                            span.style.color = '#ffffff';
                            span.style.fontWeight = 'bold';
                            span.style.padding = '12px 16px';
                            span.style.borderRadius = '4px';
                            span.style.textDecoration = 'none !important';
                            span.style.textTransform = 'uppercase';

                            range.deleteContents();
                            range.insertNode(span);
                        }
                    }),
                ]);

                return button.render();   // return button as jquery object
            }
        },
        mounted: async function() {
            this.subjectInput = this.$refs.subject;

            await this.fetchVariables();

            if (this.$route.query && this.$route.query.requiredVariables) {
                this.requiredVariables.splice(0, this.requiredVariables.length);
                const requiredVariable = {"name": this.$route.query.requiredVariables, "value": ""};
                this.requiredVariables.push(requiredVariable);
                this.variablesCopy.push(requiredVariable);
            }
            this.sortVariables();

            this.reference = this.$route.params.reference;
            this.$http.get('/guest-management/v1/establishments/' + store.currentEstablishmentReference + '/email-templates/' + this.reference).then(response => {
                this.emailTemplate = response.data;
                for (const attachmentReference of this.emailTemplate.attachmentReferences) {
                    const found = this.getFiles.filter((file) => file.reference === attachmentReference);
                    if (found.length > 0) {
                        this.attachments.push(found[0]);
                    }
                }
                this.editorElement.summernote('code', this.emailTemplate.content);
                this.changed = false;
            }).catch(error => {
                console.log(error)
            });

            const attachmentsHeight = (this.$refs.templateAttachments) ? this.$refs.templateAttachments.clientHeight : 0;
            const editorHeight = this.$refs.editor.clientHeight - this.$refs.templateMeta.clientHeight - attachmentsHeight - 81;
            console.log(editorHeight);

            (function ($, _this) {
                //Check for the text editor
                if ($.fn.summernote) {
                    let insertButtons = ['link', 'picture', 'personalisation', 'ctaStyling'];
                    if (_this.requiredVariables.length > 0) {
                        // Todo: this should be smarter, and know what to push or not push
                        insertButtons.push('formLink');
                    }

                    _this.editorElement = $(_this.$refs.textarea);  // I like to use refs instead of $(selector)
                    //Init the Awesome Text Editor
                    _this.editorElement.summernote({
                        lang: 'en-US',
                        placeholder: 'enter content here...',
                        height: editorHeight,
                        minHeight: null,
                        maxHeight: null,
                        focus: true,
                        toolbar: [
                            ['cleaner',['cleaner']], // The Button
                            ['class', ['style']],
                            ['style', ['bold', 'underline', 'clear']],
                            ['font', ['fontname', 'fontsize']],
                            ['color', ['color']],
                            ['para', ['ul', 'ol', 'paragraph', 'height']],
                            ['table', ['table']],
                            ['insert', insertButtons],
                            ['view', ['codeview']],
                            // ['view', ['codeview', 'help']],
                            ['misc', ['undo', 'redo']],
                        ],
                        fontSizes: ['8', '9', '10', '11', '12', '14', '16', '18', '20', '24', '36'],
                        buttons: {
                            personalisation: _this.personalisationButton,
                            formLink: _this.formLinkButton,
                            ctaStyling: _this.styleCTAButton
                        },
                        callbacks: {
                            onChange: function() {
                                _this.changed = true;
                            }
                        },
                        cleaner: {
                            action: 'both', // both|button|paste 'button' only cleans via toolbar button, 'paste' only clean when pasting content, both does both options.
                            icon: ' <i class="fas fa-broom"></i>',
                            keepHtml: true,
                            keepTagContents: ['span'], //Remove tags and keep the contents
                            badTags: ['applet', 'col', 'colgroup', 'embed', 'noframes', 'noscript', 'script', 'style', 'title', 'meta', 'link', 'head'], //Remove full tags with contents
                            badAttributes: ['bgcolor', 'border', 'height', 'cellpadding', 'cellspacing', 'lang', 'start', 'style', 'class', 'valign', 'width', 'data-(.*?)'], //Remove attributes from remaining tags
                            limitChars: 0, // 0|# 0 disables option
                            limitDisplay: 'both', // none|text|html|both
                            limitStop: false, // true/false
                            notTimeOut: 850, //time before status message is hidden in miliseconds
                            keepImages: true,
                            imagePlaceholder: 'https://via.placeholder.com/200'
                        }
                    });
                } else {
                    //TODO: load error message in UI
                    alert('The text editor is not loaded properly for the content.  Please contact an administrator.');
                }

            })(jQuery, this); // init jquery

            this.saveBeforeLeavingFn = async (action) => {
                if (action === "save") {
                    await this.saveContent();
                    this.$router.push({name: this.navigatingToRoute.name });
                    this.showConfirmLoseChangesDialog = false;
                } else {
                    this.showConfirmLoseChangesDialog = false;
                }
            };
            this.$root.$on('save-email-template', this.saveBeforeLeavingFn);

            this.$root.$on('finished-selecting-files', this.attachFiles);
        },
        beforeDestroy () {
            this.requiredVariables.splice(0, this.requiredVariables.length);
            jQuery('#summernote').summernote('destroy');

            this.$root.$off('save-email-template', this.saveBeforeLeavingFn);
            this.$root.$off('finished-selecting-files', this.attachFiles);
        },
        beforeRouteLeave (to, from, next) {
            if (this.changed && !this.cancelling) {
                this.navigatingToRoute = to;
                this.showConfirmLoseChangesDialog = true;
            } else {
                next();
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import "../styles/layout/forms.scss";
    @import "../styles/components/question_answer";
    @import "../styles/components/edit_email";
</style>

<style lang="scss">
    .email-editor-toolbar-dropdown {
        ul {
            list-style: none;
            margin: 0;
            padding: 0;
            /*padding: 0 10px 0 20px;*/
        }

        li {
            padding: 3px 10px 3px 20px;
        }

        li:hover {
            cursor: pointer;
            background-color: $grey-light;
        }
    }

    .email-editor-toolbar-dropdown__separator:hover {
        cursor: inherit !important;
        background-color: inherit !important;
    }

    .email-editor-toolbar-dropdown__separator {
        padding: 15px 10px 3px 20px !important;
        color: $grey-medium;
        font-style: italic;
    }

    .list-enter-active,
    .list-leave-active {
        transition: all 0.5s ease;
    }
    .list-enter-from,
    .list-leave-to {
        opacity: 0;
        transform: translateX(100px);
    }



</style>
